import { FieldDefinition, FieldOption, FieldType } from '../../models/field-definition';

const TYPE_ALIASES: Record<string, FieldType> = {
  string: FieldType.Text,
  text: FieldType.Text,
  textbox: FieldType.Text,
  list: FieldType.Dropdown,
  dropdown: FieldType.Dropdown,
  select: FieldType.Dropdown,
  bool: FieldType.Checkbox,
  boolean: FieldType.Checkbox,
  checkbox: FieldType.Checkbox,
  date: FieldType.Date,
  datetime: FieldType.DateTime,
};

function mapType(raw: unknown): FieldType {
  const key = String(raw ?? '').toLowerCase().trim();
  return TYPE_ALIASES[key] ?? FieldType.Text;
}

function normalizeOptions(raw: unknown): FieldOption[] | undefined {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.map((opt: any) => {
    if (opt != null && typeof opt === 'object') {
      const label = opt.label ?? opt.Label ?? opt.name ?? opt.Name ?? String(opt.value ?? opt.Value ?? '');
      const optionValue = opt.value ?? opt.Value ?? opt.name ?? opt.Name ?? label;
      return { label: String(label), value: optionValue };
    }
    return { label: String(opt), value: opt };
  });
}

function pick<T>(...candidates: T[]): T | undefined {
  return candidates.find(c => c !== undefined && c !== null);
}

function toNumber(raw: unknown): number | undefined {
  const value = pick(raw as any);
  if (value === undefined || value === '') {
    return undefined;
  }
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
}

function normalizeField(raw: any): FieldDefinition {
  const type = mapType(pick(raw.type, raw.Type));
  const label = String(pick(raw.label, raw.Label, raw.key, raw.Key) ?? '');
  const key = String(pick(raw.key, raw.Key) ?? '').trim() || label.replace(/\s+/g, '');

  const field: FieldDefinition = {
    key,
    label,
    type,
    required: !!pick(raw.required, raw.Required),
    defaultValue: pick(raw.defaultValue, raw.DefaultValue),
  };

  if (type === FieldType.Dropdown) {
    field.options = normalizeOptions(pick(raw.options, raw.Options)) ?? [];
  }

  if (type === FieldType.Checkbox) {
    // Older definitions store the default as "true"/"false", and the string "false" is truthy.
    field.defaultValue = field.defaultValue === true || field.defaultValue === 'true';
  }

  const minLength = toNumber(pick(raw.minLength, raw.MinLength));
  if (minLength !== undefined) { field.minLength = minLength; }

  const maxLength = toNumber(pick(raw.maxLength, raw.MaxLength));
  if (maxLength !== undefined) { field.maxLength = maxLength; }

  const min = toNumber(pick(raw.min, raw.Min));
  if (min !== undefined) { field.min = min; }

  const max = toNumber(pick(raw.max, raw.Max));
  if (max !== undefined) { field.max = max; }

  const pattern = pick(raw.pattern, raw.Pattern);
  if (pattern) { field.pattern = String(pattern); }

  const dateFormat = pick(raw.dateFormat, raw.DateFormat);
  if (dateFormat) { field.dateFormat = String(dateFormat); }

  const validationMessages = pick(raw.validationMessages, raw.ValidationMessages);
  if (validationMessages && typeof validationMessages === 'object') {
    field.validationMessages = validationMessages as Record<string, string>;
  }

  if (pick(raw.readOnly, raw.ReadOnly) !== undefined) {
    field.readOnly = !!pick(raw.readOnly, raw.ReadOnly);
  }
  if (pick(raw.disabled, raw.Disabled) !== undefined) {
    field.disabled = !!pick(raw.disabled, raw.Disabled);
  }

  field.unique = !!pick(raw.unique, raw.Unique);
  const referenceField = pick(raw.referenceField, raw.ReferenceField);
  if (referenceField) { field.referenceField = String(referenceField); }

  return field;
}

/**
 * Form definitions saved before the Case Details section became native still carry a `Binding`
 * ('caseNumber', 'hearingDateTime', 'judgeFirstName', 'judgeLastName'). The Streaming page now
 * owns those four fields itself, so a bound field is dropped here instead of being rendered -
 * otherwise an old form type would show a second Case Number next to the native one.
 * The stored fieldList is left untouched; this is a read-time filter, not a migration.
 */
function isLegacyBoundField(raw: any): boolean {
  return !!pick(raw?.binding, raw?.Binding);
}

function isHiddenField(raw: any): boolean {
  return !!pick(raw?.hidden, raw?.Hidden);
}

export function normalizeFields(raw: unknown): FieldDefinition[] {
  let source: any = raw;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(source)) {
    return [];
  }
  return source
    .filter(field => !isLegacyBoundField(field) && !isHiddenField(field))
    .map(normalizeField);
}
